import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useWebHaptics } from "web-haptics/react";
import { useLanguage } from "../contexts/LanguageContext";
import { PACKAGES, DEFAULT_PACKAGE, QUALITY_TIERS, DEFAULT_QUALITY, qualityPrice, addonTotalFor, addonPriceFor, money } from "../config/packages";
import { getFunnel, getClickIds, getAffiliateParams } from "../utils/tracking";
import { isVariantHost, mainUrl } from "../utils/variant";
import "../styles/boost.css";

// Leaving for /50off is a full page navigation, so all of this state is lost —
// a visitor pressing Back would land on a blank input step instead of the
// results they configured. Snapshot it on the way out and restore on mount.
const SNAPSHOT_KEY = "visualizer_snapshot";
const SNAPSHOT_TTL_MS = 30 * 60 * 1000;

const readSnapshot = () => {
	try {
		const raw = sessionStorage.getItem(SNAPSHOT_KEY);
		if (!raw) return null;
		const v = JSON.parse(raw);
		if (!v?.ts || Date.now() - v.ts > SNAPSHOT_TTL_MS || !v.profileData) return null;
		return v;
	} catch (e) {
		return null;
	}
};

// plansOnly renders just the plan picker, growth options and CTA — the pricing
// view. No scan, so no profile card or before/after half.
const Visualizer = ({ plansOnly = false }) => {
	const L = useLanguage();
	const { trigger: haptic } = useWebHaptics();
	const [restored] = useState(readSnapshot);
	const [username, setUsername] = useState(restored?.username || '');
	const [platform, setPlatform] = useState(restored?.platform || 'instagram');
	const [profileData, setProfileData] = useState(restored?.profileData || null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [displayedFollowers, setDisplayedFollowers] = useState(restored?.followers || 0);
	const [displayedGain, setDisplayedGain] = useState(restored?.gain || 0);
	const [displayedRangeLow, setDisplayedRangeLow] = useState(restored?.rangeLow || 0);
	const [displayedRangeHigh, setDisplayedRangeHigh] = useState(restored?.rangeHigh || 0);
	const animationRef = useRef(null);
	const gainAnimRef = useRef(null);
	const rangeLowAnimRef = useRef(null);
	const rangeHighAnimRef = useRef(null);
	const [searchResults, setSearchResults] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const searchTimeoutRef = useRef(null);
	const fetchTimeoutRef = useRef(null);

	// One VisualizerSubmit per visit: the funnel step means "this visitor used the
	// visualizer", so re-scanning another handle must not inflate the count.
	const submitFiredRef = useRef(false);
	// AddToCart fires when a plan's Start Growing is pressed — once per mount.
	const addToCartFiredRef = useRef(false);

	const [step, setStep] = useState(restored ? 'results' : 'input');
	const [scanStep, setScanStep] = useState(0);
	// Core is the default everywhere it is offered — it is the package the
	// pricing page and the funnel both preselect, so the picker should not
	// disagree with them.
	const affiliateDefaultPlan = DEFAULT_PACKAGE;
	const [selectedPlan, setSelectedPlan] = useState(restored?.selectedPlan || affiliateDefaultPlan);

	// Growth customization shown with the plan. These are real targeting options
	// (profiles carries genderTarget/countries), surfaced here so the offer feels
	// like something the visitor configures rather than a fixed package.
	// Free follower boost. The way into the plans is now claiming a boost rather
	// than a bare scan — the scan still runs underneath for the before/after
	// figures. Null while the status call is in flight; false disables the whole
	// claim step (services kill switch, or this IP already claimed) and the flow
	// falls back to the plain scan it used to be.
	const [boostEnabled, setBoostEnabled] = useState(null);
	const [boostQuantity, setBoostQuantity] = useState(0);
	const [boostResult, setBoostResult] = useState(null);
	// Drives the gift's open-and-detonate sequence. The claim itself doesn't
	// start until the animation has run, so the payoff isn't cut short by a fast
	// API response.
	const [claiming, setClaiming] = useState(false);
	const claimTimerRef = useRef(null);

	// Customization is its own screen, reached from the plan's Start Growing CTA,
	// so the plan card stays a single decision.
	const [showCustomize, setShowCustomize] = useState(false);
	// Step 2: which grade of follower, chosen after the quantity and before the
	// targeting, because it multiplies the price the next screen is priced off.
	const [showQuality, setShowQuality] = useState(false);
	const [quality, setQuality] = useState(restored?.quality || DEFAULT_QUALITY);
	// Customization is now where the add-ons are sold: Gradual delivery is
	// included, everything else is a paid switch that rides along on the
	// subscription as an upsell key.
	const [deliverySpeed, setDeliverySpeed] = useState(restored?.deliverySpeed || 'gradual');
	const [femaleOnly, setFemaleOnly] = useState(restored?.femaleOnly || false);
	const [usaOnly, setUsaOnly] = useState(restored?.usaOnly || false);

	// Add-ons are priced against the chosen package (config/packages.js), so the
	// figures move with the plan the visitor picked rather than sitting flat.
	// Upsell keys the backend prices (routes/purchase.js UPSELL_PRICES).
	const selectedAddons = [
		deliverySpeed === 'fast' && 'fast',
		femaleOnly && 'female',
		usaOnly && 'usa',
	].filter(Boolean);
	const addonTotal = addonTotalFor(selectedPlan, selectedAddons);

	// Prices and follower counts come from config/packages.js — the same source
	// the pricing page and the /50off funnel read. Variant traffic (?fromstart=1)
	// lands here instead of the pricing cards, so hardcoding them again is how
	// this screen ended up advertising the old subscription tiers.
	const visualizerPlans = PACKAGES.map((pkg) => ({
		url: pkg.key,
		name: L.visualizer.plans[pkg.key].name,
		price: pkg.price,
		wasPrice: pkg.anchor,
		monthlyGrowth: pkg.followers,
		rangeLow: pkg.rangeLow,
		rangeHigh: pkg.followers,
		tagline: L.visualizer.plans[pkg.key].tagline,
		features: L.visualizer.plans[pkg.key].features,
	}));

	const activePlan = visualizerPlans.find(p => p.url === selectedPlan) || visualizerPlans[0];

	// The claim flow narrates the boost being sent; the plain scan narrates the
	// analysis it always did.
	const scanSteps = boostEnabled
		? [
			{ icon: '🔍', text: L.visualizer.boostSteps[0] },
			{ icon: '🎁', text: L.visualizer.boostSteps[1] },
			{ icon: '🚀', text: L.visualizer.boostSteps[2] },
			{ icon: '📈', text: L.visualizer.boostSteps[3] },
		]
		: [
			{ icon: '🔍', text: L.visualizer.scanSteps[0] },
			{ icon: '📊', text: L.visualizer.scanSteps[1] },
			{ icon: '🎯', text: L.visualizer.scanSteps[2] },
			{ icon: '📈', text: L.visualizer.scanSteps[3] },
		];

	// Is the boost available to this visitor, and how many followers is it?
	// Re-checked per platform since each has its own services row.
	useEffect(() => {
		if (plansOnly) return;
		let cancelled = false;
		axios.get(`https://api.viralgpt.com/freeboost/status?platform=${platform}`, { withCredentials: true })
			.then((r) => {
				if (cancelled) return;
				const usable = !!r.data?.enabled && !r.data?.claimed;
				setBoostEnabled(usable);
				setBoostQuantity(Number(r.data?.quantity) || 0);
			})
			.catch(() => { if (!cancelled) setBoostEnabled(false); });
		return () => { cancelled = true; };
	}, [platform, plansOnly]);

	const fetchProfileData = async (uname, plat) => {
		if (!uname || !plat) {
			setError(L.visualizer.errEnterUsername);
			return;
		}

		setError(null);
		setStep('scanning');
		setScanStep(0);

		if (!submitFiredRef.current) {
			submitFiredRef.current = true;
			axios.post('https://api.viralgpt.com/pixel', {
				event: 'VisualizerSubmit',
				funnel: getFunnel(),
				...getClickIds(),
			}, { withCredentials: true }).catch(() => {});
		}

		if (typeof window !== 'undefined' && window.innerWidth < 768) {
			if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}

		const scanInterval = setInterval(() => {
			setScanStep(prev => {
				if (prev >= scanSteps.length - 1) {
					clearInterval(scanInterval);
					return prev;
				}
				return prev + 1;
			});
		}, 700);

		// Claim the free boost before the scan. It's the reason the visitor typed
		// their handle, so a failure here surfaces as the error — but a disabled
		// or already-claimed boost is not a failure, it just falls through to the
		// plans.
		let claimedHandle = uname;
		if (boostEnabled) {
			try {
				const check = await axios.post('https://api.viralgpt.com/freeboost/check', {
					platform: plat, username: uname,
				}, { withCredentials: true });
				if (check.data?.ok === false) {
					clearInterval(scanInterval);
					setError(check.data.error || L.visualizer.errFetchFailed);
					setStep('input');
					setClaiming(false);
					haptic('error');
					return;
				}
				if (check.data?.canonical) claimedHandle = check.data.canonical;

				// A private account cannot be delivered to — the panel can no more
				// see the posts than we can — so stop at the handle instead of
				// claiming a boost that then fails silently. This is the same
				// reason a paid pack bought against a private account is parked in
				// AWAITING_PUBLIC rather than sent. The visitor can make the
				// account public and submit again.
				if (check.data?.private === true) {
					clearInterval(scanInterval);
					setError(L.visualizer.boostPrivateWarning);
					setStep('input');
					setClaiming(false);
					haptic('error');
					return;
				}

				const claim = await axios.post('https://api.viralgpt.com/freeboost/claim', {
					platform: plat,
					username: claimedHandle,
					posts: check.data?.posts ?? '',
					affiliate: getAffiliateParams().affiliate || undefined,
				}, { withCredentials: true });
				setBoostResult(claim.data || null);
			} catch (e) {
				// Fail open — a boost we couldn't send must never block the plans.
				setBoostResult(null);
			}
		}

		try {
			const response = await axios.get(`https://api.viralgpt.com/auth/getProfile/${plat}/${claimedHandle}`);
			clearInterval(scanInterval);
			setScanStep(scanSteps.length);

			setTimeout(() => {
				setProfileData(response.data);
				setDisplayedFollowers(0);
				setDisplayedGain(0);
				setDisplayedRangeLow(0);
				setDisplayedRangeHigh(0);
				startCountAnimation(response.data.followers, 'followers');
				startCountAnimation(response.data.followers + activePlan.monthlyGrowth, 'gain');
				startCountAnimation(activePlan.rangeLow, 'rangeLow');
				startCountAnimation(activePlan.rangeHigh, 'rangeHigh');
				setStep('results');
				haptic('success');
			}, 600);
		} catch (err) {
			clearInterval(scanInterval);
			setError(err.response?.data?.message || L.visualizer.errFetchFailed);
			setStep('input');
			setClaiming(false);
			haptic('error');
		}
	};

	// Entry point for the CTA. With the boost live, the gift plays first and the
	// scan starts when the blast peaks; otherwise it's the plain scan.
	const GIFT_ANIMATION_MS = 1750;
	const startFlow = (uname, plat) => {
		if (!boostEnabled) {
			fetchProfileData(uname, plat);
			return;
		}
		if (claiming) return;
		setError(null);
		setClaiming(true);
		claimTimerRef.current = setTimeout(() => {
			fetchProfileData(uname, plat);
		}, GIFT_ANIMATION_MS);
	};

	const debouncedFetchProfileData = () => {
		if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
		fetchTimeoutRef.current = setTimeout(() => {
			fetchProfileData(username, platform);
		}, 500);
	};

	const startCountAnimation = (target, type, from = 0) => {
		const refs = { followers: animationRef, gain: gainAnimRef, rangeLow: rangeLowAnimRef, rangeHigh: rangeHighAnimRef };
		const setters = { followers: setDisplayedFollowers, gain: setDisplayedGain, rangeLow: setDisplayedRangeLow, rangeHigh: setDisplayedRangeHigh };
		const ref = refs[type];
		const setter = setters[type];
		if (ref.current) cancelAnimationFrame(ref.current);

		const duration = 1200;
		const start = performance.now();
		const delta = target - from;

		const animate = (currentTime) => {
			const elapsed = currentTime - start;
			const progress = Math.min(elapsed / duration, 1);
			const eased = 1 - Math.pow(1 - progress, 3);
			setter(Math.floor(from + delta * eased));
			if (progress < 1) ref.current = requestAnimationFrame(animate);
		};

		ref.current = requestAnimationFrame(animate);
	};

	const handleYouTubeSearch = async (query) => {
		if (!query) { setSearchResults([]); return; }
		try {
			const response = await axios.post('https://api.viralgpt.com/auth/search', {
				platform: 'youtube', username: query
			});
			setSearchResults(response.data.data);
		} catch (err) {
			console.error('Error searching YouTube channels:', err);
		}
	};

	const debouncedYouTubeSearch = (query) => {
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => handleYouTubeSearch(query), 500);
	};

	useEffect(() => {
		return () => {
			if (animationRef.current) cancelAnimationFrame(animationRef.current);
			if (gainAnimRef.current) cancelAnimationFrame(gainAnimRef.current);
			if (rangeLowAnimRef.current) cancelAnimationFrame(rangeLowAnimRef.current);
			if (rangeHighAnimRef.current) cancelAnimationFrame(rangeHighAnimRef.current);
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
			if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
			if (claimTimerRef.current) clearTimeout(claimTimerRef.current);
		};
	}, []);

	// Switching plans repaints the figures immediately. Animating three counters
	// for 1200ms meant a setState per frame each, re-rendering the whole tree
	// ~180 times a second — taps on the other plan buttons got swallowed. The
	// count-up still runs on the first reveal after a scan, where it earns its
	// keep. Any in-flight animation is cancelled so it can't overwrite this.
	const showCountsForPlan = (plan, followers) => {
		[gainAnimRef, rangeLowAnimRef, rangeHighAnimRef].forEach((r) => {
			if (r.current) cancelAnimationFrame(r.current);
		});
		setDisplayedGain(followers + plan.monthlyGrowth);
		setDisplayedRangeLow(plan.rangeLow);
		setDisplayedRangeHigh(plan.rangeHigh);
	};

	useEffect(() => {
		// The pricing view has no scan, so there is no follower baseline to add to.
		if (plansOnly) {
			showCountsForPlan(activePlan, 0);
			return;
		}
		if (step === 'results' && profileData) {
			showCountsForPlan(activePlan, profileData.followers);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPlan, plansOnly]);

	const resetToInput = () => {
		try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (e) { /* nothing to clear */ }
		setStep('input');
		setShowCustomize(false);
		if (claimTimerRef.current) clearTimeout(claimTimerRef.current);
		setClaiming(false);
		setBoostResult(null);
		setProfileData(null);
		setUsername('');
		setSearchQuery('');
		setError(null);
		setSearchResults([]);
		window.scrollTo({ top: document.getElementById('visualizer')?.offsetTop - 80, behavior: 'smooth' });
	};

	// Written on the way to /50off: the snapshot restores this screen on Back, the
	// handoff carries the configured targeting into checkout.
	const persistHandoff = () => {
		try {
			// Restored if they press Back — see readSnapshot().
			sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
				platform,
				username,
				profileData,
				selectedPlan,
				quality,
				deliverySpeed,
				femaleOnly,
				usaOnly,
				followers: profileData?.followers || 0,
				gain: (profileData?.followers || 0) + activePlan.monthlyGrowth,
				rangeLow: activePlan.rangeLow,
				rangeHigh: activePlan.rangeHigh,
				ts: Date.now(),
			}));
		} catch (e) { /* sessionStorage unavailable — Back just resets */ }
		try {
			localStorage.setItem('visualizer_handoff', JSON.stringify({
				platform,
				username: profileData?.username || username,
				// Carried so checkout can apply and charge for what was configured
				// here. addons are the paid switches; the targeting fields are the
				// profile-record shapes they imply.
				deliverySpeed,
				addons: selectedAddons,
				// The grade chosen at step 2. Checkout re-prices from it and sends
				// the key on; the server multiplies its own base price by it.
				quality,
				genderTarget: femaleOnly ? 'women' : 'both',
				countries: usaOnly ? 'us' : 'global',
				ts: Date.now(),
			}));
		} catch (e) { /* localStorage unavailable — skip */ }
	};

	const checkoutHref = isVariantHost
		? mainUrl(`/50off?plan=${activePlan.url}&from=viz`)
		: `/50off?plan=${activePlan.url}&from=viz`;

	// Paid on/off switch for an add-on. Every upgrade on this screen — targeting
	// and delivery speed alike — is the same shape: the plan already does the
	// off state, so the only question is whether to pay for the on state.
	const AddonSwitch = ({ icon, title, subtitle, checked, onChange, addonKey }) => (
		<div
			onClick={() => { haptic(15); onChange(!checked); }}
			className="vg-addon-switch flex items-center gap-3 p-3 mb-3 rounded-xl cursor-pointer transition-all duration-200"
			style={{
				border: checked ? '2px solid #635bff' : '2px solid #e3e8ee',
				background: checked ? 'rgba(99,91,255,0.05)' : '#fff',
				boxShadow: checked ? '0 4px 12px rgba(99,91,255,0.12)' : 'none',
			}}
		>
			<span className="vg-addon-icon text-xl leading-none flex-shrink-0">{icon}</span>
			<div className="min-w-0 flex-1">
				<div className="vg-addon-title text-sm font-bold" style={{ color: '#0a2540' }}>{title}</div>
				<div className="text-xs" style={{ color: '#8898aa' }}>{subtitle}</div>
			</div>
			<span className="vg-addon-price text-xs font-bold flex-shrink-0" style={{ color: '#635bff' }}>
				{L.visualizer.addonPlus(addonPriceFor(selectedPlan, addonKey).toFixed(2))}
			</span>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				aria-label={title}
				onClick={(e) => { e.stopPropagation(); haptic(15); onChange(!checked); }}
				className="vg-addon-toggle flex-shrink-0"
				style={{
					width: 44, height: 26, padding: 3, border: 'none', borderRadius: 999, cursor: 'pointer',
					background: checked ? '#635bff' : '#c9d2e0', transition: 'background 0.2s ease',
				}}
			>
				<span style={{
					display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff',
					transform: checked ? 'translateX(18px)' : 'translateX(0)', transition: 'transform 0.2s ease',
				}}></span>
			</button>
		</div>
	);

	const platforms = [
		{ id: 'instagram', icon: 'fa-instagram', label: 'Instagram', color: '#E4405F' },
		{ id: 'tiktok', icon: 'fa-tiktok', label: 'TikTok', color: '#000' },
		{ id: 'x', icon: 'fa-x-twitter', label: 'X', color: '#000' },
		// { id: 'youtube', icon: 'fa-youtube', label: 'YouTube', color: '#FF0000' },  // temporarily disabled — re-enable when YouTube scraper is reliable again
	];

	const growthPct = profileData ? Math.min(Math.round((activePlan.monthlyGrowth / profileData.followers) * 100), 999) : 0;

	return (
		<>
		{/* Full-screen payoff, timed to the gift's jackpot frame. */}
		{claiming && (
			<>
				<div className="vg-flash vg-show" aria-hidden="true"></div>
				<div className="vg-boost-text vg-show" aria-hidden="true">{L.visualizer.boostWord}</div>
			</>
		)}
		{/* Scroll anchor for resetToInput. Only the full instance claims it:
		    /pricing mounts a plansOnly picker above the claim widget, and two
		    elements sharing an id would send the reset to the wrong one. */}
		<div id={plansOnly ? undefined : 'visualizer'}></div>
		{/* The plansOnly picker mounts directly under the fixed navbar on /pricing,
		    so its top spacing is responsive and lives in .stripe-section--picker —
		    an inline style can't carry a media query, and would override the class.
		    The full widget keeps the flat 1.5rem: it opens on the claim UI, which
		    has content of its own up there. */}
		<div
			className={`stripe-section stripe-section--gradient${plansOnly ? ' stripe-section--picker' : ''}`}
			style={plansOnly ? undefined : { paddingTop: '1.5rem' }}
		>
			<div className="px-4" style={{ maxWidth: '560px', margin: '0 auto' }}>

				{/* INPUT STEP */}
				{/* boostEnabled is null until the status call lands. Rendering the
				    input step before then showed the plain scan UI for a frame and
				    then swapped it for the claim UI — hold until we know which one
				    this visitor gets. */}
				{!plansOnly && step === 'input' && boostEnabled === null && (
					<div style={{ minHeight: '420px' }} aria-hidden="true"></div>
				)}

				{!plansOnly && step === 'input' && boostEnabled !== null && (
					<div className={claiming ? 'vg-claiming' : ''} style={{ animation: 'fadeSlideIn 0.5s ease both' }}>
						{boostEnabled ? (
							<div className="text-center mb-5">
								{/* The gift is the whole pitch — it wiggles until tapped, then
								    spins and detonates. */}
								<div className="vg-gift-stage">
									<img
										className={`vg-gift-icon${claiming ? ' vg-opening' : ''}`}
										src="/images/boost/gift.png"
										alt=""
									/>
									<div className={`vg-burst${claiming ? ' vg-show' : ''}`} aria-hidden="true">
										{['✨', '⭐', '🪙', '🎉', '⚡', '💫', '🎊', '💰', '✨', '⭐', '🪙', '🎉', '⚡', '💫', '🎊', '💰'].map((e, i) => (
											<span key={i}>{e}</span>
										))}
									</div>
								</div>
								<div className="vg-ticket-brand">
									<span className="vg-line-1">{L.visualizer.boostBrandLine1}</span>
									<span className="vg-line-2">{L.visualizer.boostBrandLine2}</span>
								</div>
								<p className="stripe-body stripe-body--sm stripe-text-muted mx-auto" style={{ maxWidth: '420px' }}>
									{L.visualizer.boostSubheading}
								</p>
							</div>
						) : (
							<div className="text-center mb-6 md:mb-8">
								<span className="stripe-badge-green inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-5" style={{
									letterSpacing: '0.1em', textTransform: 'uppercase',
								}}>
									<span className="w-1.5 h-1.5 rounded-full" style={{ background: '#1a7f4b', animation: 'pulse 2s infinite' }}></span>
									{L.visualizer.badge}
								</span>
								<h2 className="stripe-heading stripe-heading--lg text-2xl sm:text-3xl md:text-4xl mb-3">
									{L.visualizer.headingJSX}
								</h2>
								<p className="stripe-body stripe-body--sm stripe-text-muted mx-auto" style={{ maxWidth: '420px' }}>
									{L.visualizer.subheading}
								</p>
							</div>
						)}

						{/* Platform Selector */}
						<div className="text-center mb-3">
							<span className="text-xs font-semibold uppercase" style={{ color: '#8898aa', letterSpacing: '1px' }}>{L.visualizer.choosePlatform}</span>
						</div>
						<div className="grid grid-cols-3 gap-2 mb-5">
							{platforms.map((p) => (
								<button
									key={p.id}
									className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-all duration-200 cursor-pointer"
									style={{
										border: platform === p.id ? '2px solid #635bff' : '2px solid #e3e8ee',
										background: platform === p.id ? 'rgba(99,91,255,0.05)' : '#fff',
										boxShadow: platform === p.id ? '0 4px 16px rgba(99,91,255,0.12)' : 'none',
										transform: platform === p.id ? 'translateY(-2px)' : 'none',
									}}
									onClick={() => {
										haptic(15);
										setPlatform(p.id);
										setSearchQuery('');
										if (p.id === 'youtube') setUsername('');
										setProfileData(null);
										setSearchResults([]);
									}}
								>
									<i className={`fab ${p.icon} text-xl`} style={
										p.id === 'instagram'
											? {
												background: 'linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)',
												WebkitBackgroundClip: 'text',
												backgroundClip: 'text',
												WebkitTextFillColor: 'transparent',
												opacity: platform === p.id ? 1 : 0.45,
											}
											: { color: platform === p.id ? p.color : '#8898aa' }
									}></i>
									<span className="text-xs font-semibold" style={{ color: platform === p.id ? '#0a2540' : '#8898aa' }}>{p.label}</span>
								</button>
							))}
						</div>

						{/* Username Input */}
						{/* min-w-0 on the row and the input matters: a flex item's default
						    min-width is auto, so the text input's intrinsic size refused to
						    shrink and pushed the non-shrinking CTA off-screen on phones. */}
						<div className="flex items-center rounded-2xl mb-3 transition-all duration-200 w-full min-w-0" style={{
							background: '#fff', border: '2px solid #e3e8ee', padding: '4px 4px 4px 16px', maxWidth: '100%',
						}}
						onFocus={(e) => { e.currentTarget.style.borderColor = '#635bff'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(99,91,255,0.08)'; }}
						onBlur={(e) => { e.currentTarget.style.borderColor = '#e3e8ee'; e.currentTarget.style.boxShadow = 'none'; }}
						>
							<span className="text-lg flex-shrink-0" style={{ color: '#8898aa' }}>@</span>
							<input
								type="text"
								placeholder={L.visualizer.handlePlaceholder}
								className="flex-1 min-w-0 py-3 px-2 text-base outline-none bg-transparent"
								style={{ color: '#0a2540' }}
								value={platform === 'youtube' ? searchQuery || username : username}
								onChange={(e) => {
									if (platform === 'youtube') {
										setSearchQuery(e.target.value);
										debouncedYouTubeSearch(e.target.value);
									} else {
										setUsername(e.target.value);
									}
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										if (platform === 'youtube') handleYouTubeSearch(searchQuery);
										else if (username && platform) startFlow(username, platform);
									}
								}}
							/>
							{platform === 'youtube' ? (
								<button
									className="px-3 md:px-5 py-3 rounded-xl text-xs md:text-sm font-bold text-white flex-shrink-0 whitespace-nowrap cursor-pointer transition-all duration-200 hover:opacity-90"
									style={{ background: '#635bff' }}
									onClick={() => { haptic('nudge'); handleYouTubeSearch(searchQuery); }}
								>
									{L.visualizer.search}
								</button>
							) : (
								<button
									className="px-3 md:px-5 py-3 rounded-xl text-xs md:text-sm font-bold text-white flex-shrink-0 whitespace-nowrap cursor-pointer transition-all duration-200 hover:opacity-90"
									style={{ background: '#635bff' }}
									disabled={!username || !platform || claiming}
									onClick={() => { haptic('nudge'); startFlow(username, platform); }}
								>
									{boostEnabled ? L.visualizer.boostCta : L.visualizer.analyze}
								</button>
							)}
						</div>

						{platform === 'youtube' && searchResults?.length > 0 && (
							<div className="rounded-xl p-3 mb-3 overflow-y-auto" style={{ maxHeight: '180px', background: '#fff', border: '1px solid #e3e8ee', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
									{searchResults.map((channel) => (
										<div
											key={channel.id}
											className="flex flex-col items-center p-2.5 rounded-xl cursor-pointer transition-all duration-150 hover:bg-gray-50"
											style={{ border: '1px solid #e3e8ee' }}
											onClick={() => {
												haptic(15);
												setUsername(channel.title);
												setSearchQuery(channel.title);
												setSearchResults([]);
												fetchProfileData(channel.channelId, 'youtube');
											}}
										>
											<img src={channel.thumbnail?.[0]?.url} alt={channel.title} className="w-10 h-10 rounded-full mb-1.5 object-cover" onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }} />
											<span className="text-xs font-medium text-center truncate w-full" style={{ color: '#0a2540' }}>{channel.title}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{error && (
							<div className="text-center mb-4 py-2.5 px-4 rounded-xl" style={{ background: '#fff0f0', border: '1px solid #fecaca' }}>
								<span className="text-sm" style={{ color: '#dc2626' }}><i className="fas fa-exclamation-circle mr-1.5"></i>{error}</span>
							</div>
						)}

						<div className="flex justify-center gap-4 text-xs" style={{ color: '#8898aa' }}>
							<span className="flex items-center gap-1">🔒 {L.visualizer.noPassword}</span>
							<span className="flex items-center gap-1">
								{boostEnabled ? `🎁 ${L.visualizer.boostFineprint}` : `⚡ ${L.visualizer.takesSeconds}`}
							</span>
						</div>
					</div>
				)}

				{/* SCANNING STEP */}
				{!plansOnly && step === 'scanning' && (
					<div className="text-center py-8 md:py-12" style={{ animation: 'fadeSlideIn 0.3s ease both' }}>
						{boostEnabled ? (
							/* The bolt carries the wait — shaking, strobing, ringed by
							   spinning rays while the order goes out. */
							<div className="vg-bolt-stage mb-2">
								<div className="vg-bolt-glow"></div>
								<div className="vg-bolt-rays" aria-hidden="true">
									{Array.from({ length: 8 }, (_, i) => <span key={i}></span>)}
								</div>
								<div className="vg-sparkles" aria-hidden="true">
									{Array.from({ length: 6 }, (_, i) => <span key={i}>✨</span>)}
								</div>
								<img className="vg-bolt-emoji" src="/images/boost/bolt.png" alt="" />
							</div>
						) : (
							<div className="mx-auto mb-6" style={{
								width: '64px', height: '64px', borderRadius: '50%',
								border: '3px solid #e3e8ee', borderTopColor: '#635bff',
								animation: 'spin 0.8s linear infinite',
							}}></div>
						)}
						<p className="text-base font-semibold mb-6" style={{ color: '#0a2540' }}>
							{boostEnabled ? L.visualizer.boostActivating : L.visualizer.analyzingAccount}
						</p>

						<div className="flex flex-col gap-3 mx-auto" style={{ maxWidth: '280px' }}>
							{scanSteps.map((s, i) => {
								const isDone = i < scanStep;
								const isActive = i === scanStep;
								return (
									<div key={i} className="flex items-center gap-2.5 text-sm transition-all duration-300" style={{
										opacity: isDone || isActive ? 1 : 0.3,
										color: isDone ? '#1a7f4b' : isActive ? '#635bff' : '#8898aa',
									}}>
										<span className="text-base w-5 text-center">{isDone ? '✅' : s.icon}</span>
										<span className="font-medium">{s.text}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* QUALITY STEP — after the quantity, before the targeting. It
				    multiplies the price, so choosing speed and audience against a
				    figure that is about to change would read as a bait. */}
				{showQuality && (
					<div style={{ animation: 'fadeSlideIn 0.4s ease both' }}>
						<div className="text-center mb-3">
							<h2 className="stripe-heading text-2xl sm:text-3xl mb-0">{L.fiftyoff.quality.heading}</h2>
						</div>

						<div className="rounded-2xl p-4 mb-4" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(99,91,255,0.06)' }}>
							{QUALITY_TIERS.map((tier) => {
								const copy = L.fiftyoff.quality.tiers[tier.key];
								const isActive = quality === tier.key;
								return (
									<button
										key={tier.key}
										type="button"
										onClick={() => { haptic(15); setQuality(tier.key); }}
										aria-pressed={isActive}
										className="w-full text-left rounded-xl cursor-pointer transition-all duration-200"
										style={{
											display: 'block', padding: '12px 14px', marginBottom: 8,
											border: isActive ? '2px solid #635bff' : '2px solid #e3e8ee',
											background: isActive ? 'rgba(99,91,255,0.05)' : '#fff',
										}}
					>
						<span className="flex items-center justify-between gap-2">
							<span className="flex items-center gap-2 min-w-0">
								<span className="text-sm font-bold" style={{ color: '#0a2540' }}>{copy.name}</span>
												{/* The long description lives behind this rather than on the
												    card. Three paragraphs stacked is a wall to read before a
												    decision, and the checkmarks below already carry the
												    reasons. stopPropagation so reading it does not also
												    pick the tier. */}
												<span
													className="vg-tip flex-shrink-0"
													onClick={(e) => e.stopPropagation()}
												>
													<i className="fa-regular fa-circle-question" style={{ color: '#8898aa', fontSize: '0.8rem' }}></i>
													<span className="vg-tip-body">{copy.desc}</span>
												</span>
												<span className="flex-shrink-0" style={{
													fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
													padding: '3px 8px', borderRadius: 999,
													color: isActive ? '#fff' : '#635bff',
													background: isActive ? 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)' : 'rgba(99,91,255,0.10)',
												}}>{copy.badge}</span>
											</span>
											{/* The increment over the package, not the tier total: the
											    package price is already accepted, so the question here is
											    "is this tier worth $10 more", the same framing the add-on
											    switches use on the next step. The base tier is Included
											    rather than +$0.
											    Shown as the .99 just under the real increment (+$9.99 for
											    a $10.00 step) so it reads as a price. Display only: the
											    tier total and the charge are unchanged, so the checkout
											    figure is a cent above the sum of what the cards say. */}
											<span className="flex-shrink-0 font-extrabold" style={{ color: isActive ? '#635bff' : '#0a2540' }}>
												{tier.multiplier === 1
													? L.visualizer.addonIncluded
													: L.visualizer.addonPlus(money(Math.ceil(Math.round((qualityPrice(activePlan.price, tier.key) - activePlan.price) * 100) / 100) - 0.01))}
											</span>
										</span>
										{copy.features.map((f, i) => (
											<span key={i} className="flex items-start gap-1.5 mt-1">
												<i className="fa-solid fa-check" style={{ color: '#1a7f4b', fontSize: '0.65rem', marginTop: 3 }}></i>
												<span className="text-xs" style={{ color: '#425466' }}>{f}</span>
											</span>
										))}
									</button>
								);
							})}

							<button
								type="button"
								onClick={() => { haptic('nudge'); setShowQuality(false); setShowCustomize(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
								className="block w-full text-center py-3.5 mt-2 rounded-xl text-base font-bold text-white transition-all duration-200 hover:shadow-lg cursor-pointer border-none"
								style={{ background: 'linear-gradient(135deg, #635bff 0%, #9854ff 50%, #d345c6 100%)' }}
							>
								{L.visualizer.customizeContinue}
							</button>
						</div>

					</div>
				)}

				{/* CUSTOMIZE STEP — its own screen, entered from the quality step */}
				{showCustomize && (
					<div style={{ animation: 'fadeSlideIn 0.4s ease both' }}>
						<div className="text-center mb-3">
							<h2 className="stripe-heading text-2xl sm:text-3xl mb-2">{L.visualizer.customizeTitle}</h2>
							<p className="stripe-body stripe-body--sm stripe-text-muted mx-auto" style={{ maxWidth: '420px' }}>
								{L.visualizer.customizeSubtitle}
							</p>
						</div>

						<div className="rounded-2xl p-4 mb-4" style={{
							background: '#fff', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(99,91,255,0.06)',
						}}>
							<div className="text-xs font-bold uppercase mb-2" style={{ color: '#8898aa', letterSpacing: '1.5px' }}>
								{L.visualizer.deliverySpeedLabel}
							</div>
							{/* Gradual is the plan's own speed and costs nothing, so it was
							    never a choice worth showing — a two-option picker made the
							    included default look like a decision to weigh against the
							    paid one. Same switch as the targeting add-ons: off is what
							    the plan already does, on is the upgrade. `deliverySpeed`
							    keeps its 'gradual'/'fast' values because the checkout
							    handoff and the persisted draft both read them. */}
							<AddonSwitch
								icon="🚀"
								addonKey="fast"
								title={L.visualizer.speedFast}
								subtitle={L.visualizer.speedFastSub}
								checked={deliverySpeed === 'fast'}
								onChange={(on) => setDeliverySpeed(on ? 'fast' : 'gradual')}
							/>
							<div className="text-xs font-bold uppercase mb-2" style={{ color: '#8898aa', letterSpacing: '1.5px' }}>
								{L.visualizer.targetingLabel}
							</div>
							<AddonSwitch
								icon="👩"
								addonKey="female"
								title={L.visualizer.femaleOnlyTitle}
								subtitle={L.visualizer.femaleOnlySub}
								checked={femaleOnly}
								onChange={setFemaleOnly}
							/>
							<AddonSwitch
								icon="🇺🇸"
								addonKey="usa"
								title={L.visualizer.usaOnlyTitle}
								subtitle={L.visualizer.usaOnlySub}
								checked={usaOnly}
								onChange={setUsaOnly}
							/>


							{/* Checkout always lives on the main site — from the variant
							    domain, mainUrl() forwards r/fbclid/ttclid + the entry marker. */}
							<a
								href={checkoutHref}
								onClick={() => { haptic('nudge'); persistHandoff(); }}
								className="block w-full text-center py-3.5 mt-4 rounded-xl text-base font-bold text-white transition-all duration-200 hover:shadow-lg no-underline"
								style={{ background: 'linear-gradient(135deg, #635bff 0%, #9854ff 50%, #d345c6 100%)' }}
							>
								{L.visualizer.customizeContinue}
							</a>
						</div>

						{!plansOnly && (
							<div className="flex justify-center gap-5 text-xs mb-3 stripe-text-muted">
								<span className="flex items-center gap-1">🔒 {L.visualizer.noPasswordRequired}</span>
							</div>
						)}

					</div>
				)}

				{/* RESULTS STEP */}
				{!showCustomize && !showQuality && (plansOnly || (step === 'results' && profileData)) && (
					<div style={{ animation: 'fadeSlideIn 0.5s ease both' }}>
						{plansOnly && (
							<div className="text-center mb-4">
								<div style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '7px 10px', fontSize: 'clamp(0.62rem, 3vw, 0.76rem)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', maxWidth: '100%' }}>
									{L.packages.valueProp}
								</div>
								<div className="d-flex flex-wrap align-items-center justify-content-center" style={{ gap: 8, marginTop: 10, color: '#68778d', fontSize: '0.72rem' }}>
									<span><span aria-hidden="true" style={{ color: '#facc15', marginRight: 4 }}>★</span><strong style={{ color: '#0a2540' }}>{L.header.proofRating}</strong> ({L.header.proofReviews})</span>
									<span style={{ color: '#cbd5e1' }}>•</span>
									<strong style={{ color: '#0a2540' }}>{L.header.proofFollowers}</strong>
								</div>
							</div>
						)}
						{!plansOnly && boostResult && (
							<div className="rounded-2xl px-4 py-3 mb-3 text-center" style={{
								background: 'linear-gradient(135deg, rgba(26,127,75,0.08), rgba(99,91,255,0.08))',
								border: '1px solid rgba(26,127,75,0.2)',
							}}>
								<div className="text-sm font-bold" style={{ color: '#1a7f4b' }}>
									{boostResult.claimed
										? `🎁 ${L.visualizer.boostDelivered((boostResult.quantity || boostQuantity).toLocaleString())}`
										: L.visualizer.boostAlreadyClaimed}
								</div>
							</div>
						)}

						{!plansOnly && (
							<>
						{/* Profile Card */}
						<div className="flex items-center gap-4 p-5 rounded-2xl mb-3" style={{
							background: '#fff', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(99,91,255,0.06)',
						}}>
							<div className="flex-shrink-0" style={{
								padding: '2.5px', borderRadius: '50%',
								background: 'linear-gradient(135deg, #635bff, #9854ff, #d345c6)',
							}}>
								{profileData.photo ? (
									<img src={profileData.photo} alt="" className="rounded-full object-cover" style={{ width: '64px', height: '64px', border: '2.5px solid #fff' }} onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }} />
								) : (
									<div className="rounded-full flex items-center justify-center text-2xl" style={{ width: '64px', height: '64px', background: '#E8E4FF', border: '2.5px solid #fff' }}>😎</div>
								)}
							</div>
							<div className="min-w-0">
								<div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: '#8898aa' }}>
									<i className={`fab fa-${platform === 'x' ? 'x-twitter' : platform}`}></i>
									{platforms.find(p => p.id === platform)?.label}
								</div>
								<div className="text-base font-semibold truncate" style={{ color: '#0a2540' }}>@{profileData.username}</div>
								<div className="text-xs mt-0.5" style={{ color: '#8898aa' }}>
									{L.visualizer.currentlyFollowers(displayedFollowers.toLocaleString())}
								</div>
							</div>
						</div>

						{/* Before / After */}
						<div className="grid grid-cols-2 gap-3 mb-3">
							<div className="rounded-2xl px-5 py-3 text-center" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
								<div className="text-xs font-bold uppercase mb-1" style={{ color: '#8898aa', letterSpacing: '1.5px' }}>{L.visualizer.before}</div>
								<div className="text-2xl sm:text-3xl font-bold" style={{ color: '#0a2540', letterSpacing: '-0.5px' }}>{displayedFollowers.toLocaleString()}</div>
								<div className="text-xs mt-0.5" style={{ color: '#8898aa' }}>{L.visualizer.currentFollowers}</div>
							</div>
							<div className="rounded-2xl px-5 py-3 text-center relative overflow-hidden" style={{
								background: 'linear-gradient(135deg, rgba(99,91,255,0.06), rgba(152,84,255,0.06))',
								border: '1px solid rgba(99,91,255,0.12)',
							}}>
								<div className="absolute -top-5 -right-5 w-20 h-20 rounded-full" style={{ background: 'linear-gradient(135deg, #635bff, #9854ff, #d345c6)', opacity: 0.06 }}></div>
								<div className="text-xs font-bold uppercase mb-1" style={{
									letterSpacing: '1.5px',
									background: 'linear-gradient(135deg, #635bff, #9854ff, #d345c6)',
									WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
								}}>{L.visualizer.after30Days}</div>
								<div className="text-2xl sm:text-3xl font-bold flex items-center justify-center gap-2" style={{ letterSpacing: '-0.5px' }}>
									<i className="fa-solid fa-arrow-trend-up" style={{ color: '#1a7f4b', fontSize: '1.4rem' }}></i>
									<span style={{
										background: 'linear-gradient(135deg, #635bff, #9854ff, #d345c6)',
										WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
									}}>{displayedGain.toLocaleString()}</span>
								</div>
								<div className="text-xs mt-0.5" style={{ color: 'rgba(99,91,255,0.6)' }}>{L.visualizer.projectedFollowers}</div>
							</div>
						</div>
							</>
						)}

						{/* Quantity picker. Full-width rows rather than three columns:
						    the choice is just how many followers, so each option gets
						    room to read naturally and stays comfortable on a phone.
						    No plan names, no feature lists — quantity and price only. */}
						<div className="flex flex-col gap-2 mb-4">
							{visualizerPlans.map((p) => {
								const isActive = selectedPlan === p.url;
								const meta = PACKAGES.find((pkg) => pkg.key === p.url) || {};
								const isPopular = !!meta.popular;
								return (
									<button
										key={p.url}
										type="button"
										onClick={() => { haptic(15); setSelectedPlan(p.url); }}
										aria-pressed={isActive}
										className="w-full flex items-center justify-between gap-3 py-3.5 px-4 rounded-xl cursor-pointer transition-all duration-200 text-left"
										style={{
										border: isActive ? '3px solid #635bff' : isPopular ? '2px solid #b9afff' : '2px solid #e3e8ee',
										background: isActive ? 'linear-gradient(90deg, rgba(99,91,255,0.09), rgba(152,84,255,0.04))' : '#fff',
										boxShadow: isActive ? '0 8px 22px rgba(99,91,255,0.20)' : isPopular ? '0 3px 10px rgba(99,91,255,0.08)' : 'none',
										}}
									>
										<span className="flex items-center gap-2 min-w-0">
											{/* Radio dot — makes it read as a choice, not a button. */}
											<span
												className="flex-shrink-0 rounded-full"
												style={{
													width: 18, height: 18,
													border: isActive ? '5px solid #635bff' : '2px solid #c9d2e0',
													background: '#fff',
												}}
											></span>
											<span className="min-w-0">
												<span className="block text-base font-bold" style={{ color: '#0a2540', lineHeight: 1.2 }}>
													{L.visualizer.customizeFollowerCount(p.monthlyGrowth.toLocaleString())}
												</span>
												{(meta.popular || meta.bestValue) && (
													<span
														className="inline-block text-xs font-bold mt-1"
														style={{ color: meta.popular ? '#635bff' : '#0a2540' }}
													>
														{meta.popular ? L.packages.mostPopular : L.packages.bestValue}
													</span>
												)}
											</span>
										</span>
									<span className="flex-shrink-0 d-flex align-items-baseline" style={{ gap: 6 }}>
										<span style={{ color: '#94a3b8', fontSize: '0.76rem', textDecoration: 'line-through', fontWeight: 600 }}>${p.wasPrice}</span>
										{/* Selection only. This used to also go blue for `isPopular`,
										    which left the $9.99 row's price in the selected colour
										    permanently — pick any other pack and two prices looked
										    chosen at once. Popular is already carried by its border,
										    larger price and label; it must not borrow the one colour
										    that means "this is the one you picked". */}
										<span className="font-extrabold" style={{ color: isActive ? '#635bff' : '#0a2540', letterSpacing: '-0.5px', fontSize: isPopular ? '1.4rem' : '1.25rem' }}>${p.price}</span>
									</span>
									</button>
								);
							})}
						</div>

						{/* Straight from the selection to the CTA. The chosen row already
						    shows the quantity and the price, so restating them in a card
						    below only asked the visitor to read the same decision twice. */}
						<button
							type="button"
							onClick={() => {
								haptic('nudge');
								// Committing to a plan is the add-to-cart moment: a package is
								// chosen and the flow turns toward checkout. Once per mount, so
								// bouncing between quality and the picker can't refire it.
								if (!addToCartFiredRef.current) {
									addToCartFiredRef.current = true;
									axios.post('https://api.viralgpt.com/pixel', {
										event: 'AddToCart',
										funnel: getFunnel(),
										...getClickIds(),
										...getAffiliateParams(),
									}, { withCredentials: true }).catch(() => {});
								}
								// Quality comes before targeting: it re-prices the order, and
								// choosing speed and audience against a figure that is about to
								// change reads as a bait.
								setShowQuality(true);
								window.scrollTo({ top: 0, behavior: 'smooth' });
							}}
							className="block w-full text-center py-3.5 mb-4 rounded-xl text-base font-bold text-white transition-all duration-200 hover:shadow-lg cursor-pointer border-none"
							style={{ background: 'linear-gradient(135deg, #635bff 0%, #9854ff 50%, #d345c6 100%)', marginBottom: plansOnly ? 0 : undefined }}
						>
							{L.visualizer.choosePlan}
						</button>

						{!plansOnly && (
							<div className="flex justify-center gap-5 text-xs mb-3 stripe-text-muted">
								<span className="flex items-center gap-1">🔒 {L.visualizer.noPasswordRequired}</span>
							</div>
						)}

						{!plansOnly && (
							<button onClick={() => { haptic(15); resetToInput(); }} className="flex items-center gap-1.5 mt-4 text-sm cursor-pointer bg-transparent border-none transition-colors duration-200 mx-auto" style={{ color: '#8898aa' }}>
								{L.visualizer.tryDifferent}
							</button>
						)}
					</div>
				)}

			</div>
		</div>

		<style>{`
			@keyframes fadeSlideIn {
				from { opacity: 0; transform: translateY(16px); }
				to { opacity: 1; transform: translateY(0); }
			}
			@keyframes spin {
				to { transform: rotate(360deg); }
			}
			@keyframes pulse {
				0%, 100% { transform: scale(1); opacity: 1; }
				50% { transform: scale(0.6); opacity: 0.4; }
			}
			/* The quality tiers' long descriptions, behind a question mark. Shown
			   on hover and on focus, so it is reachable by keyboard and not only
			   by mouse. */
			.vg-tip { position: relative; display: inline-flex; cursor: help; }
			.vg-tip-body {
				position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
				width: 240px; padding: 10px 12px; border-radius: 10px;
				background: #0a2540; color: #fff; font-size: 0.72rem; line-height: 1.5;
				text-align: left; font-weight: 400; white-space: normal;
				opacity: 0; visibility: hidden; transition: opacity 0.15s ease;
				z-index: 20; box-shadow: 0 8px 24px rgba(10,37,64,0.25);
			}
			.vg-tip-body::after {
				content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
				border: 6px solid transparent; border-top-color: #0a2540;
			}
			.vg-tip:hover .vg-tip-body, .vg-tip:focus-within .vg-tip-body { opacity: 1; visibility: visible; }
			/* Near the screen edge on a phone the tooltip would overflow; clamp it
			   to the card instead of letting it push the layout sideways. */
			@media (max-width: 480px) {
				.vg-tip-body { width: 200px; left: 0; transform: none; }
				.vg-tip-body::after { left: 12px; transform: none; }
			}
			/* Targeting cards have a fixed price and switch on the right. Tightening
			   only their mobile chrome gives headings such as “USA-only followers”
			   enough width to stay on one line, which also makes the section shorter. */
			@media (max-width: 575px) {
				.vg-addon-switch { gap: 0.5rem !important; padding: 10px !important; }
				.vg-addon-icon { font-size: 1rem !important; }
				.vg-addon-title { white-space: nowrap; font-size: 0.78rem !important; }
				.vg-addon-price { font-size: 0.68rem !important; }
				.vg-addon-toggle { width: 40px !important; height: 24px !important; }
				.vg-addon-toggle span { width: 18px !important; height: 18px !important; }
				.vg-addon-toggle[aria-checked="true"] span { transform: translateX(16px) !important; }
			}
		`}</style>
		</>
	);
};

export default Visualizer;
